"use client";
import { useEffect, useState } from "react";
import Select, { SingleValue } from "react-select";
import { useLocations } from "../../hooks/useLocations";
import { TLocation } from "../../types/Locations";
import { useNavigate } from "react-router-dom";

interface LocationSelectorProps {
  onChange: (newValue: SingleValue<TLocation>, actionMeta: any) => void;
  value: string | null;
  inRegionId?: string | null;
  disabled?: boolean;
  onAddNewLocation?: () => void; // Callback for when "Add New Location" is clicked
}

const LocationSelector: React.FC<LocationSelectorProps> = ({
  onChange,
  value,
  disabled = false,
  onAddNewLocation,
}) => {
  const { locations: data, isLoading } = useLocations();
  const [mounted, setMounted] = useState(false);
  const navigate = useNavigate();

  // Create special "Add New Location" option
  const addNewLocationOption = {
    _id: "__add_new__",
    name: "Add a New Location",
    address: "",
    city: "",
    state: "",
    zip: "",
    country: "",
    lon: 0,
    lat: 0,
    devices: [],
    color: "#3B82F6"
  };

  // Combine options with "Add New Location" at the top
  const options = mounted ? [addNewLocationOption, ...(data || [])] : [];

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleChange = (selectedOption: SingleValue<TLocation>, actionMeta: any) => {
    if (selectedOption?._id === "__add_new__") {
      // Call the callback if provided, otherwise navigate directly
      if (onAddNewLocation) {
        onAddNewLocation();
      } else {
        navigate("/locations");
      }
      return;
    }
    onChange(selectedOption, actionMeta);
  };

  if (!mounted) return null; // Prevents SSR mismatch

  return (
    <>
      <Select
        classNamePrefix="select"
        getOptionValue={(opt) => opt._id}
        getOptionLabel={(opt) => opt.name}
        isClearable
        isLoading={isLoading}
        value={data?.find((l) => l._id === value)}
        onChange={handleChange}
        options={options}
        isDisabled={disabled}
        placeholder="Select a location"
        styles={{
          input: (provided) => ({
            ...provided,
            fontSize: window.innerWidth < 768 ? '16px' : '14px', // Prevent zoom on mobile
          }),
          singleValue: (provided) => ({
            ...provided,
            fontSize: window.innerWidth < 768 ? '16px' : '14px',
            textTransform: 'none', // Prevent uppercase transformation
          }),
          placeholder: (provided) => ({
            ...provided,
            fontSize: window.innerWidth < 768 ? '16px' : '14px',
          }),
          option: (provided, state) => ({
            ...provided,
            textTransform: 'none', // Prevent uppercase transformation
            ...(state.data?._id === "__add_new__" && {
              color: '#1D4ED8', // Blue color for "Add a New Location"
              fontWeight: '600',
              backgroundColor: state.isFocused ? '#EFF6FF' : provided.backgroundColor,
            }),
          }),
        }}
      />
      {/* <pre>
        {value}
        {JSON.stringify(
          data.find((l) => l._id === value),
          null,
          2
        )}
      </pre> */}
    </>
  );
};

export default LocationSelector;
